import { useState, useEffect } from "react";
import { useInterviewContext } from "../contexts/InterviewContext";
import useMediaRecorder from "../hooks/useMediaRecorder";

const VoiceRecorder = ({ onTranscriptionComplete }) => {
  const {
    setIsLoading,
    setError,
    isLoading,
    error: contextError,
  } = useInterviewContext();

  const [richTranscriptionData, setRichTranscriptionData] = useState(null);
  const [localError, setLocalError] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);

  const handleRecordingStop = async (audioBlob, audioUrlFromHook) => {
    if (!audioBlob) return;

    setIsLoading((prev) => ({ ...prev, transcription: true }));
    setError(null);
    setLocalError("");
    setRichTranscriptionData(null);
    onTranscriptionComplete(audioUrlFromHook, recordingTime, audioBlob);
    setIsLoading((prev) => ({ ...prev, transcription: false }));
  };

  const { isRecording, startRecording, stopRecording, audioUrl } =
    useMediaRecorder(handleRecordingStop);

  const handleStartRecording = () => {
    setRichTranscriptionData(null);
    setLocalError("");
    setError(null);
    setRecordingTime(0);
    startRecording();
  };

  useEffect(() => {
    let timerId;
    if (isRecording) {
      timerId = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
    }
    return () => clearInterval(timerId);
  }, [isRecording]);

  useEffect(() => {}, [audioUrl]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const cleanTranscript = richTranscriptionData?.fullTranscript || "";
  const hasRecording = audioUrl && !isRecording;
  const hasTranscript = cleanTranscript && !isLoading.transcription;
  const showError = (localError || contextError) && !isLoading.transcription;

  return (
    // Removed max-w-2xl and mx-auto, as parent component (InterviewPage) now controls width via its own max-w-4xl.
    // The main container here will be for internal padding and structure if needed, but backgrounds come from parent.
    <div>
      {/* Main Recording Interface */}
      {/* Removed specific background, shadow, border from this div. It will inherit parent's card style. */}
      {/* Added text-slate-200 as base text color for this component's content. */}
      <div className="p-6 sm:p-8 text-slate-200">
        {!isRecording && !hasRecording && (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-6 shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  fill="white"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">
                Record Your Response
              </h2>
              <p className="text-slate-300 text-lg">
                Click the button below to start recording.
              </p>
            </div>

            <button
              onClick={handleStartRecording}
              disabled={isLoading.transcription}
              className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center mx-auto"
            >
              Start Recording
            </button>
          </div>
        )}

        {isRecording && (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center mb-6 shadow-xl animate-pulse">
                <div className="w-8 h-8 bg-white/90 rounded-md" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">
                Recording...
              </h2>
              <div className="text-4xl font-mono text-red-400 mb-3">
                {formatTime(recordingTime)}
              </div>
              <p className="text-slate-300 text-lg">Speak clearly and naturally.</p>
            </div>

            <button
              onClick={stopRecording}
              disabled={isLoading.transcription}
              className="group px-8 py-4 bg-gradient-to-r from-red-500 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300  disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center mx-auto"
            >
              Stop Recording
            </button>
          </div>
        )}

        {hasRecording && !isRecording && (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-6 shadow-xl">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  fill="white"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">
                Recording Complete
              </h2>
              <p className="text-slate-300 text-lg">
                {formatTime(recordingTime)} recorded.
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleStartRecording}
                className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center mx-auto"
              >
                Record Again
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading.transcription && (
          <div className="text-center py-10">
            <div className="w-12 h-12 mx-auto mb-4"> {/* Adjusted size */}
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-400 border-t-transparent"></div>
            </div>
            <p className="text-slate-300 font-medium text-lg">
              Processing your speech...
            </p>
          </div>
        )}
      </div>

      {/* Audio Playback */}
      {hasRecording && (
        <div className="mt-8 bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-slate-700">
          <h3 className="text-xl font-semibold text-white mb-4">
            Your Recording
          </h3>
          <div className="bg-slate-700/60 rounded-lg p-4">
            <audio controls src={audioUrl} className="w-full">
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      )}

      {/* Transcription */}
      {hasTranscript && (
        <div className="mt-8 bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white">
              Transcription
            </h3>
            <button
              onClick={() => navigator.clipboard.writeText(cleanTranscript)}
              className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-1.5 transition-colors group"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                className="group-hover:text-blue-300 transition-colors"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy
            </button>
          </div>
          <div className="bg-slate-700/60 rounded-lg p-4 border border-slate-600">
            <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">
              {cleanTranscript}
            </p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {showError && (
        <div className="mt-8 bg-red-900/30 border border-red-700/50 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" // Slightly larger icon
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-red-300 text-sm">
              {localError ||
                "An error occurred during transcription. You can still listen to your recording."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
